import { MatIconButton } from '@angular/material/button';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  InjectionToken,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  signal,
  ViewChildren,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { Router } from '@angular/router';
import { AsyncPipe } from '@angular/common';

import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';
import {
  MatAutocomplete,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';

import {
  CdkVirtualScrollViewport,
  ScrollingModule,
} from '@angular/cdk/scrolling';

import {
  debounceTime,
  map,
  Observable,
  of,
  startWith,
  Subscription,
  switchMap,
  tap,
} from 'rxjs';

import { HotelsBookingProviderService } from './hotels-booking-provider.service';
import {
  ACCOMONDATION,
  FACILITIES,
  PRICE,
  SCORE,
  SORTING_OPTIONS,
  STARS,
} from './utils';
import { ESvgIcons } from 'src/app/core/enums/svg-icons';
import { ERoutes } from 'src/app/core/enums/routes.enum';

import { AuthService } from 'src/app/core/services/auth.service';
import { BreakpointService } from 'src/app/core/services/breakpoint.service';
import { FavoritesProviderService } from 'src/app/core/services/favorites-provider.service';
import { TripWizardStateService } from '../../components/trip-wizard/trip-wizard-state.service';

import { HotelsMapComponent } from '../../maps/hotels-map/hotels-map.component';
import { AuthDialogComponent } from '../../dialogs/auth-dialog/auth-dialog.component';
import { FiltersWrapperComponent } from '../../components/filters-wrapper/filters-wrapper.component';
import {
  BookingHotelCardComponent,
  IHotelItem,
} from '../../components/booking-hotel-card/booking-hotel-card.component';
import {
  OverlayPanel,
  BOTTOM_LEFT_POSITION,
} from 'src/app/core/services/overlay-panel';
import { NoItemsFoundComponent } from '../../components/no-items-found/no-items-found.component';
import { HotelPriceRangePanelComponent } from './hotel-price-range-panel/hotel-price-range-panel.component';
import { HotelListMobileComponent } from '../../../modules/countries/components/mobile/hotel-list-mobile/hotel-list-mobile.component';

import moment from 'moment';

export const HOTEL_CLIENT = new InjectionToken('HOTEL_CLIENT');

export interface IHotelClient {
  getHotelsCollection: (
    cityId: string,
    dates: {
      start: string;
      end: number;
    },
    payload?: object,
    itineraryIndex?: number,
  ) => Observable<IHotelItem[]>;
}

@Component({
  selector: 'app-hotels-booking-container',
  imports: [
    BookingHotelCardComponent,
    MatCheckboxModule,
    MatSelectModule,
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    MatIconModule,
    ScrollingModule,
    MatIconButton,
    FiltersWrapperComponent,
    HotelsMapComponent,
    HotelListMobileComponent,
    NoItemsFoundComponent,
    MatAutocomplete,
    MatAutocompleteTrigger,
    AsyncPipe,
  ],
  templateUrl: './hotels-booking-container.component.html',
  styleUrl: './hotels-booking-container.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [HotelsBookingProviderService],
})
export class HotelsBookingContainerComponent implements OnInit, OnDestroy {
  @Input() public showFilters = true;
  @Input() public itineraryIndex: number = -1;
  @Input() public partialFilters = false;
  @Input() public showInfoBlueLabel = false;

  public currentCity: any;
  public currentCountry: any;
  public hotelRows = signal([]);
  public hotelsOnMap = signal([]);
  public hotelsAmount: number = 0;
  public currentIndex: number = 0;
  public maxScrollIndex: number = 0;
  public scrollByRows: number = 2;
  public mapVisibleItems: number = 6;
  public searchControl: FormControl = new FormControl('');
  public filterForm: FormGroup = new FormGroup({
    stars: new FormControl([]),
    score: new FormControl(),
    sort: new FormControl(),
    type: new FormControl(),
    refundable: new FormControl(false),
    breakfastIncluded: new FormControl(false),
    facilities: new FormControl([]),
    priceRange: new FormGroup({
      min: new FormControl(),
      max: new FormControl(),
    }),
  });
  public isFetching = signal(true);
  public visiblePriceRanges = { min: 1, max: 1000 };
  public fetchingItems: any[][] = [
    [1, 2, 3, 4],
    [1, 2, 3, 4],
    [1, 2, 3, 4],
  ];
  public cacheHotelItems = [];
  public filteredHotels: Observable<any[]>;
  public openedMap = false;
  public openedFilters = true;
  public starsOptions = STARS;
  public scoreOptions = SCORE;
  public accomondationOptions = ACCOMONDATION;
  public facilitiesOptions = FACILITIES;
  public priceOptions = PRICE;
  public sortingOptions = SORTING_OPTIONS;
  public eSvgIcons = ESvgIcons;

  @ViewChildren(CdkVirtualScrollViewport)
  private viewPorts: QueryList<CdkVirtualScrollViewport>;
  #subs: Subscription;

  constructor(
    protected readonly breakpointService: BreakpointService,
    protected readonly authService: AuthService,
    private readonly cdr: ChangeDetectorRef,
    private readonly matDialog: MatDialog,
    private readonly r: Router,
    private readonly overlayService: OverlayPanel,
    private readonly tripWizardStateService: TripWizardStateService,
    protected readonly favoritesProviderService: FavoritesProviderService,
    @Inject(HOTEL_CLIENT) private readonly hotelsBookingProvider: IHotelClient,
  ) {}

  @Input({ required: true })
  public set city(value: any) {
    if (!value) return;
    this.currentCity = value;
    if (this.filterForm) {
      this.filterForm.reset(null, { emitEvent: false });
      this.cdr.markForCheck();
    }
    this.setupHotelsForView(
      this.cleanObject(this.filterForm.getRawValue()),
      true,
    );
  }

  @Input({ required: true })
  public set country(value: any) {
    if (!value) return;
    this.currentCountry = value;
  }

  public get getSortControl(): FormControl {
    return this.filterForm.get('sort') as FormControl;
  }

  generateArr(length: number): number[] {
    return new Array(length).fill(0);
  }

  public toggleAllHotelsOnMap(): void {
    this.openedMap = !this.openedMap;
    this.setViewRowsConfigsByParams();
    if (!this.openedMap) {
      this.clearHighlightClasses();
    }
    this.cdr.markForCheck();
  }

  public toggleShowFiltersOnBox(): void {
    this.openedFilters = !this.openedFilters;
    this.setViewRowsConfigsByParams();
    this.cdr.markForCheck();
  }

  public ngOnInit() {
    this.#subs = this.filterForm.valueChanges
      .pipe(debounceTime(300))
      .subscribe((filterData) => {
        this.setupHotelsForView(this.cleanObject(filterData), false);
      });

    this.priceOptions = PRICE.map((i) => ({
      text: this.authService.apiSettingsService.currencySymbol + i.text,
      value: i.value,
    }));

    this.filteredHotels = this.searchControl.valueChanges.pipe(
      startWith(''),
      map((value: string) => this._filter(value || '')),
    );
  }

  public ngOnDestroy() {
    if (this.#subs) {
      this.#subs.unsubscribe();
    }
  }

  public getFormattedPriceRange(): string {
    const min = this.filterForm.value.priceRange.min;
    const max = this.filterForm.value.priceRange.max;
    const symbol = this.authService.apiSettingsService.currencySymbol;
    if (min) {
      return symbol + min + ' ~ ' + symbol + max;
    } else {
      return '';
    }
  }

  public showPriceRangePanel(origin: HTMLDivElement): void {
    const panelRef = this.overlayService.open(HotelPriceRangePanelComponent, {
      origin: origin,
      position: BOTTOM_LEFT_POSITION,
      data: {
        min: this.visiblePriceRanges.min,
        max: this.visiblePriceRanges.max,
      },
    });
    const subs = panelRef
      .valueChanged()
      .subscribe((priceRange: { min: number; max: number }) => {
        this.filterForm.patchValue({ priceRange });
      });
    panelRef.afterClosed().subscribe(() => subs.unsubscribe());
  }

  public getCustomTriggerText(
    control: AbstractControl,
    options: any[],
  ): string {
    const value = control.value;
    if (value && value.length) {
      const mappedOptions = options
        .filter((i) => value.includes(i.value))
        .map((i) => i.text);
      if (value.length < 3) {
        return mappedOptions.join(', ');
      } else {
        const amount = mappedOptions.length - 2;
        return mappedOptions.slice(0, 2).join(', ') + `, ${amount} other(s)`;
      }
    }
    return '';
  }

  public showFavouriteHotels() {
    setTimeout(() => {
      if (this.authService.userId) {
        this.r.navigate([ERoutes.SLASH, ERoutes.ACCOUNT, ERoutes.MY_TRIPS]);
      } else {
        this.matDialog
          .open(AuthDialogComponent, {
            width: '1150px',
            maxWidth: '98vw',
            maxHeight: '98dvh',
            panelClass: 'mobile-container',
            data: {
              type: 'favourite',
            },
          })
          .afterClosed()
          .pipe(
            switchMap((isClose) => {
              if (!isClose) return of(null);
              return this.authService.waitForUserId().pipe(
                tap(() => {
                  this.r.navigate([
                    ERoutes.SLASH,
                    ERoutes.ACCOUNT,
                    ERoutes.MY_TRIPS,
                  ]);
                }),
              );
            }),
          )
          .subscribe();
      }
    }, 300);
  }

  public scrollTo(value: number, autoScroll: boolean = false): void {
    this.currentIndex += value;
    this.viewPorts.forEach((i: CdkVirtualScrollViewport) => {
      i.scrollToIndex(this.currentIndex, 'smooth');
    });
    this.getItemsForPage(this.currentIndex / this.scrollByRows);
    if (autoScroll) return;
    this.clearHighlightClasses();
  }

  public setHighlightBySelectedIndex(
    hotelId: number,
    autoScroll: boolean = false,
  ): void {
    this.hotelRows().forEach((row) => {
      row.forEach((hotel: any, index: number) => {
        if (hotel.id === hotelId) {
          hotel.class = 'highlight';
          if (autoScroll) {
            setTimeout(() => {
              this.currentIndex = index;
              this.scrollTo(0, true);
            }, 250);
          }
        } else {
          hotel.class = 'not-highlight';
        }
      });
    });
  }

  public handleClearSearchControl(): void {
    this.searchControl.setValue('', { onlySelf: true });
    this.clearHighlightClasses();
  }

  private getItemsForPage(pageIndex: number): void {
    const startIndex = pageIndex * this.mapVisibleItems;
    const endIndex = startIndex + this.mapVisibleItems;
    this.hotelsOnMap.set(this.cacheHotelItems.slice(startIndex, endIndex));
  }

  private setupHotelsForView(filterData: any, init: boolean) {
    this.isFetching.set(true);

    const { isSpecifyDates, tripStartDate, tripEndDate, numDays } =
      this.tripWizardStateService.currentTripStateValue;

    const start: number = isSpecifyDates
      ? moment(tripStartDate).unix()
      : moment(new Date()).add(1, 'day').unix();

    const end: number = isSpecifyDates
      ? moment(tripEndDate).unix()
      : moment(new Date()).add(numDays, 'days').unix();

    if (this.currentCity && this.currentCity.id) {
      this.hotelsBookingProvider
        .getHotelsCollection(
          this.currentCity.id,
          { start: `${start}`, end: end },
          filterData,
          this.itineraryIndex,
        )
        .subscribe((r) => {
          this.isFetching.set(false);
          this.hotelsAmount = r.length;
          this.splitArrayIntoRows(r, init);
          this.getItemsForPage(0);
          this.currentIndex = 0;
          this.cdr.markForCheck();
        });
    }
  }

  private splitArrayIntoRows(items: any[], init: boolean) {
    const chunkSize = Math.ceil(items.length / 3);
    const rows = [];
    for (let i = 0; i < 3; i++) {
      rows.push(items.slice(i * chunkSize, (i + 1) * chunkSize));
    }
    this.hotelRows.set(rows);
    this.parseToFlatArray(rows, init);
    this.maxScrollIndex = Math.ceil(chunkSize);
  }

  private parseToFlatArray(matrix: any[][], init: boolean): void {
    let maxPrice: number | null = null;
    let minPrice: number | null = null;
    const result: any[] = [];
    const maxLength = Math.max(...matrix.map((row) => row.length));

    for (let colStart = 0; colStart < maxLength; colStart++) {
      for (const row of matrix) {
        if (row[colStart] !== undefined) {
          const item = row[colStart];
          result.push(row[colStart]);

          if (item && item.price) {
            const { totalAmount } = item.price;
            maxPrice = !maxPrice
              ? totalAmount
              : Math.max(maxPrice, totalAmount);
            minPrice = !minPrice
              ? totalAmount
              : Math.min(minPrice, totalAmount);
          }
        }
      }
    }

    if (init) {
      this.filterForm.get('priceRange').patchValue(
        {
          min: !minPrice ? 1 : Math.ceil(minPrice),
          max: Math.ceil(maxPrice),
        },
        { emitEvent: false },
      );
      this.visiblePriceRanges.min = Math.ceil(minPrice);
      this.visiblePriceRanges.max = Math.ceil(maxPrice);
    }
    this.cacheHotelItems = result;
  }

  private clearHighlightClasses(): void {
    this.hotelRows().forEach((row) => {
      row.forEach((hotel: any) => (hotel.class = ''));
    });
  }

  private cleanObject(formData: any) {
    const cleanedObj: Record<string, any> = {};

    Object.keys(formData).forEach((key) => {
      const val = formData[key];
      if (val !== undefined && val !== null) {
        if (val instanceof Array && val.length > 0) {
          cleanedObj[key] = val;
        }
      }
    });
    if (formData.priceRange) {
      if (formData.priceRange.min) {
        cleanedObj.priceRange = {
          min: formData.priceRange.min,
        };
      }
      if (formData.priceRange.max) {
        cleanedObj.priceRange.max = formData.priceRange.max;
      }
    }
    if (formData.sort) cleanedObj.sort = formData.sort;
    if (formData.score) cleanedObj.score = formData.score;
    if (formData.refundable) cleanedObj.refundable = formData.refundable;
    if (formData.breakfastIncluded)
      cleanedObj.breakfastIncluded = formData.breakfastIncluded;
    return cleanedObj;
  }

  private setViewRowsConfigsByParams(): void {
    if (!this.openedFilters) {
      this.scrollByRows = 2;
      this.mapVisibleItems = 6;
    } else if (this.openedFilters && this.openedMap) {
      this.scrollByRows = 1;
      this.mapVisibleItems = 3;
    } else {
      this.scrollByRows = 2;
      this.mapVisibleItems = 6;
    }
  }

  private _filter(value: string): string[] {
    const filterValue = value.toLowerCase();

    return this.cacheHotelItems.filter((option) =>
      option.name.toLowerCase().includes(filterValue),
    );
  }

  private setEmptyHotelsToMakeEqual(items: any[]): any[] {
    if (!items.length) return [];
    if (items.length % 3 === 0) return items;

    while (items.length % 3 !== 0) {
      items.push({ isEmpty: true });
    }

    return items;
  }
}
